import React from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  FormDialogActions,
  FormDialogTitle,
  FormElement,
  FormInputsStack,
  FormCancelButton,
  FormSubmitButton,
  useForm,
  RichTextEditor,
} from '@marageti/z4-lib';
import { Controller, SubmitHandler, FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

const schema = z.object({
  content: z.string(),
});

type EditRichTextFieldModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  label: string;
  value: string;
  onSave: (value: string) => void;
};

const EditRichTextFieldModal = ({
  open,
  onClose,
  title,
  label,
  value,
  onSave,
}: EditRichTextFieldModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('desktop'));
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      content: value || '',
    },
  });

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    onSave(data.content);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <FormDialogTitle handleClose={onClose}>
        {title}
      </FormDialogTitle>
      <FormElement name="edit-rich-text-field-form" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ paddingTop: 1 }}>
          <Typography variant="body" color="primary" sx={{ mb: 2 }}>
            These preferences improve trip matching.
          </Typography>
          <FormInputsStack>
            <Controller
              control={control}
              name="content"
              render={({ field }) => (
                <RichTextEditor
                  defaultValue={value || ''}
                  editorName="richTextField"
                  error={!!errors.content}
                  helperText={errors.content?.message as string}
                  label={label}
                  onChange={field.onChange}
                  sx={{
                    maxHeight: { mobile: 'calc(100vh - 256px)', desktop: 'calc(100vh - 300px)' },
                    minHeight: '350px',
                    flex: 1,
                  }}
                  useLinks
                  useLists
                />
              )}
            />
          </FormInputsStack>
        </DialogContent>
        <FormDialogActions>
          <FormCancelButton onClick={onClose} />
          <FormSubmitButton>
            Save
          </FormSubmitButton>
        </FormDialogActions>
      </FormElement>
    </Dialog>
  );
};

export default EditRichTextFieldModal;
